import { Check as CheckIcon, Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import { add, format } from "date-fns";
import React, { createContext, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { allOrdersMealsPath, allOrdersPath, allOrdersPricePath, allOrdersRemarksPath, CLASSROOM_CLOSED_RANGE_PATH, menuPath, MODEL_COMPLEX_PATH, SCHOOL_CLOSED_RANGE_PATH, studentsPath, userOrdersMealsPath, userOrdersPathDate, userOrdersSnacksPath } from "../../functions/src/admin/paths";
import { useOrderHistoryMonths } from "../api/useOrderHistoryMonths";
import { canNavigateToNextOrderWeek, dayNum, getWeekDays, toDateFromServer, toDayString, toServerFromDate } from "../../functions/src/api/dates";
import { hasClosureAffectingPlacedOrder, type ClassroomClosedRangeRecord, type SchoolClosedRangeRecord } from "../../functions/src/api/orderClosures";
import { isDayOrderingUiBlocked, type OrderDayDeadlineContext } from "../../functions/src/common/orderDeadlines";
import type { ModelComplexity, Order } from "../../functions/src/types";
import { hasUnsubmittedOrderChanges, isChangeOrderEmptyDraft } from "./orderChangingDraft";
import { auth, currentMenu, DataSnapshot } from "../api/firebase";
import { useDatabase, useDatabaseVal, useList } from "../api/firebaseHooks";
import { MealLine, SnackLine, Student } from "../types";
import ClosureAttentionIcon from "./ClosureAttentionIcon";
import { useOrderStrings } from "./order-strings";
import { useOrderDayDeadlineContext } from "./useOrderDayDeadlineContext";
import { useOrderDeadlineAt } from "./useOrderDeadlineAt";

const HOURS_TO_ENABLE_PERIOD = 0;

/** Keep in sync with `.day-selector-selection-line` height in order-app.css */
const DAY_SELECTOR_SELECTION_LINE_HEIGHT_PX = 3;
/** Fallback before the portaled badge is measured (see `DayMobileStatusPortal`). */
const DAY_SELECTOR_STATUS_ICON_SIZE_PX = 20;
/** Optical align: circle center on the sliding line (line sits on the button border). */
const DAY_SELECTOR_STATUS_ICON_VERTICAL_NUDGE_PX = 1;

const DaySelectorDaysTrackContext = createContext<React.RefObject<HTMLDivElement | null> | null>(null);

type DayMobileStatusPortalProps = {
    anchorRef: React.RefObject<HTMLButtonElement | null>
    layoutKey: string
    children: React.ReactNode
};

/** Mobile status badges must paint above the sliding selection line (a sibling layer on the track). */
function DayMobileStatusPortal({ anchorRef, layoutKey, children }: DayMobileStatusPortalProps) {
    const trackRef = React.useContext(DaySelectorDaysTrackContext);
    const iconWrapRef = useRef<HTMLDivElement | null>(null);
    const [position, setPosition] = useState<{ left: number; bottom: number } | null>(null);

    const updatePosition = useCallback(() => {
        const anchor = anchorRef.current;
        const track = trackRef?.current;
        if (!anchor || !track) {
            setPosition(null);
            return;
        }
        const anchorRect = anchor.getBoundingClientRect();
        const trackRect = track.getBoundingClientRect();
        const lineCenterFromTrackBottom =
            DAY_SELECTOR_SELECTION_LINE_HEIGHT_PX / 2;
        const iconHeight =
            iconWrapRef.current?.getBoundingClientRect().height
            || DAY_SELECTOR_STATUS_ICON_SIZE_PX;
        const iconBottomFromTrackBottom =
            lineCenterFromTrackBottom
            - iconHeight / 2
            + DAY_SELECTOR_STATUS_ICON_VERTICAL_NUDGE_PX;
        setPosition({
            left: anchorRect.left - trackRect.left + anchorRect.width / 2,
            bottom: iconBottomFromTrackBottom,
        });
    }, [anchorRef, trackRef]);

    useLayoutEffect(() => {
        updatePosition();
    }, [layoutKey, updatePosition, children]);

    useLayoutEffect(() => {
        if (!position || !iconWrapRef.current) return;
        updatePosition();
    }, [position?.left, updatePosition]);

    useEffect(() => {
        const track = trackRef?.current;
        if (!track) return undefined;
        const observer = new ResizeObserver(() => updatePosition());
        observer.observe(track);
        const iconEl = iconWrapRef.current;
        if (iconEl) observer.observe(iconEl);
        window.addEventListener("resize", updatePosition);
        return () => {
            observer.disconnect();
            window.removeEventListener("resize", updatePosition);
        };
    }, [trackRef, updatePosition, position?.left]);

    if (!children || !position || !trackRef?.current) return null;

    return createPortal(
        <div
            ref={iconWrapRef}
            className="mobile-status-icon mobile-status-icon--portaled"
            style={{
                position: "absolute",
                left: position.left,
                bottom: position.bottom,
                transform: "translateX(-50%)",
                zIndex: 3,
            }}
        >
            {children}
        </div>,
        trackRef.current,
    );
}

/**
 * Week nav chevron (same path as /img-assets/arrow-right-white.svg) drawn inline with `currentColor`
 * instead of a white <img>: in Windows High Contrast / forced-colors mode the button background is
 * replaced by ButtonFace and images are not recolored, so a white image became invisible.
 */
function NavArrow({ direction }: { direction: 'back' | 'fwd' }) {
    return <svg
        className="day-selector-nav-icon"
        viewBox="0 0 7 12"
        width="7.23"
        height="12"
        aria-hidden="true"
        focusable="false"
        style={direction === 'back' ? { transform: 'rotate(180deg)' } : undefined}
    >
        <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M1.2 1.2 L5.8 6 L1.2 10.8" />
    </svg>;
}

export default function DaySelector({ date, cannotOrder, onChange, navigationDisabled }: DaySelectorProps) {

    const orderStrings = useOrderStrings();
    const deadlineAt = useOrderDeadlineAt();
    const { uid } = auth.currentUser;
    const dateNow = new Date();
    const orderClockAt = deadlineAt ?? dateNow;
    const [modelConstants] = useDatabaseVal<ModelComplexity>(MODEL_COMPLEX_PATH);
    const [week, setWeek] = useState(getWeekDays(date || dateNow));

    const schoolClosedRangesSnap = useDatabase(SCHOOL_CLOSED_RANGE_PATH);
    const classroomClosedRangesSnap = useDatabase(CLASSROOM_CLOSED_RANGE_PATH);
    const schoolClosedRangesList = useList(schoolClosedRangesSnap);
    const classroomClosedRangesList = useList(classroomClosedRangesSnap);
    const schoolClosedRangeRecords = useMemo((): SchoolClosedRangeRecord[] => (
        schoolClosedRangesList?.map(s => s.val() as SchoolClosedRangeRecord) ?? []
    ), [schoolClosedRangesList]);
    const classroomClosedRangeRecords = useMemo((): ClassroomClosedRangeRecord[] => (
        classroomClosedRangesList?.map(s => s.val() as ClassroomClosedRangeRecord) ?? []
    ), [classroomClosedRangesList]);
    const orderPeople = useDatabase(uid ? studentsPath(uid) : null);
    const orderPeopleList = useList(orderPeople);
    const deadlineCtx = useOrderDayDeadlineContext();

    // Update week when date changes
    useEffect(() => {
        if (date) {
            const newWeek = getWeekDays(date);
            setWeek(newWeek);
        }
    }, [date]);

    const isDateInPast = toDateFromServer(week[4]) < toDateFromServer(toServerFromDate(dateNow));
    const datesNextWeek = useMemo(() => {
        const dateNextWeek = toDayString(add(toDateFromServer(week[0]), { days: 7 }));
        return getWeekDays(dateNextWeek);
    }, [week[0]]);
    const menuRootSnap = useDatabase(menuPath(currentMenu));
    // Check if any day in the coming week has a menu in place
    const hasMenuNextWeek = useMemo(
        () => datesNextWeek.some((day) => menuRootSnap?.child(day).exists()),
        [menuRootSnap, datesNextWeek],
    );

    //const hasMenuNextWeek = useDatabase(menuPath(`${currentCatering}/${dateNextWeek}`))?.exists();
    const isEndofPeriod = !canNavigateToNextOrderWeek(week[0], modelConstants?.orderFutureWeeks, orderClockAt);
    const enableFwdDate = isDateInPast || ((!modelConstants?.allowMeals || hasMenuNextWeek) && !isEndofPeriod);
    const historyMonths = useOrderHistoryMonths();
    const enableBwdDate = toDateFromServer(week[0]) > add(dateNow, { months: -historyMonths });

    const onDaysBack = () => {
        if (navigationDisabled || !onChange) return;
        const firstMon = add(toDateFromServer(week[0]), { days: -7 });
        setWeek(getWeekDays(firstMon));
        onChange(toDayString(add(firstMon, { days: 4 })));
    }
    const onDaysFwd = () => {
        if (navigationDisabled || !onChange) return;
        const firstMon = add(toDateFromServer(week[0]), { days: 7 });
        setWeek(getWeekDays(firstMon));
        onChange(toDayString(firstMon));
    }

    const daysTrackRef = useRef<HTMLDivElement | null>(null);
    const selectedDayRef = useRef<HTMLButtonElement | null>(null);
    const [selectionLine, setSelectionLine] = useState<{ left: number; width: number } | null>(null);

    const updateSelectionLine = useCallback(() => {
        const track = daysTrackRef.current;
        const button = selectedDayRef.current;
        if (!track || !button) {
            setSelectionLine(null);
            return;
        }
        const trackRect = track.getBoundingClientRect();
        const buttonRect = button.getBoundingClientRect();
        setSelectionLine({
            left: buttonRect.left - trackRect.left,
            width: buttonRect.width,
        });
    }, []);

    useLayoutEffect(() => {
        updateSelectionLine();
    }, [date, week, updateSelectionLine]);

    useEffect(() => {
        const track = daysTrackRef.current;
        if (!track) return undefined;
        const observer = new ResizeObserver(() => updateSelectionLine());
        observer.observe(track);
        window.addEventListener("resize", updateSelectionLine);
        return () => {
            observer.disconnect();
            window.removeEventListener("resize", updateSelectionLine);
        };
    }, [updateSelectionLine]);

    return <div
        className="day-selector"
    >
        <button type="button" onClick={onDaysBack} disabled={!enableBwdDate || navigationDisabled} className="day-selector-nav day-selector-nav--back" aria-label={orderStrings.previousWeek}><NavArrow direction="back" /></button>
        <DaySelectorDaysTrackContext.Provider value={daysTrackRef}>
            <div className="day-selector-days" ref={daysTrackRef}>
                {week.map(day => (
                    <Day
                        key={day}
                        day={day}
                        selected={day === date}
                        onClick={onChange}
                        navigationDisabled={navigationDisabled}
                        dayButtonRef={day === date ? selectedDayRef : undefined}
                        schoolClosedRangeRecords={schoolClosedRangeRecords}
                        classroomClosedRangeRecords={classroomClosedRangeRecords}
                        orderPeopleList={orderPeopleList}
                        deadlineCtx={deadlineCtx}
                    />
                ))}
                {selectionLine && (
                    <div
                        className="day-selector-selection-line"
                        aria-hidden="true"
                        style={{ left: selectionLine.left, width: selectionLine.width }}
                    />
                )}
            </div>
        </DaySelectorDaysTrackContext.Provider>
        <button type="button" onClick={onDaysFwd} disabled={!enableFwdDate || navigationDisabled} className="day-selector-nav day-selector-nav--fwd" aria-label={orderStrings.nextWeek}><NavArrow direction="fwd" /></button>
    </div>
}

export type DaySelectorProps = {
    date: string
    cannotOrder: boolean
    onChange?: (date: string) => void
    navigationDisabled?: boolean
}

type DayProps = {
    day: string
    selected: boolean
    onClick?: (date: string) => void
    navigationDisabled?: boolean
    dayButtonRef?: React.Ref<HTMLButtonElement>
    schoolClosedRangeRecords: SchoolClosedRangeRecord[]
    classroomClosedRangeRecords: ClassroomClosedRangeRecord[]
    orderPeopleList: DataSnapshot[]
    deadlineCtx: OrderDayDeadlineContext | null
}

function Day({ day, selected, onClick, navigationDisabled, dayButtonRef, schoolClosedRangeRecords, classroomClosedRangeRecords, orderPeopleList, deadlineCtx }: DayProps) {

    const dayButtonLocalRef = useRef<HTMLButtonElement | null>(null);
    const mergeDayButtonRef = useCallback((el: HTMLButtonElement | null) => {
        dayButtonLocalRef.current = el;
        if (dayButtonRef) {
            (dayButtonRef as React.MutableRefObject<HTMLButtonElement | null>).current = el;
        }
    }, [dayButtonRef]);

    const orderStrings = useOrderStrings();
    const deadlineAt = useOrderDeadlineAt();
    const date = toDateFromServer(day);
    const { uid } = auth.currentUser;

    const dayName = orderStrings.weekdayAbbr[dayNum(day) - 1];

    const dayOfMonth = format(date, 'dd');

    const monthName = orderStrings.monthAbbr[+format(date, 'M') - 1];

    const [placedPrice] = useDatabaseVal<number>(day && allOrdersPricePath(uid, day) || null);
    const [orderRemarks] = useDatabaseVal<string>(day && allOrdersRemarksPath(uid, day) || null);
    const placedOrderMeals = useDatabase(day && allOrdersMealsPath(uid, day) || null);
    const placedOrderMealsList: DataSnapshot<MealLine>[] = useList(placedOrderMeals);
    const placedOrder = useDatabase(day && allOrdersPath(uid, day) || null);
    const menu1Key: string = useDatabase(`${menuPath(currentMenu)}/${day}/menu1/key`)?.val();

    // Αν κάνω uncomment αυτό,για κάποιο λόγο κρασάρει
    const userOrder = useDatabase(day && userOrdersPathDate(uid, day) || null);
    let hasOrderRemark = false;
    if (deadlineCtx?.modelConstants?.allowCommentsPerOrder) {
        hasOrderRemark = !!userOrder?.child('orderRemarks').val();
    }

    const isChangingOrder = userOrder?.child('isChangingOrder')?.val() === true;
    const cannotOrder = !deadlineAt || (deadlineCtx ? isDayOrderingUiBlocked(day, deadlineCtx, isChangingOrder, deadlineAt) : true);
    const hasPlacedOrder = !isChangingOrder && (typeof placedPrice === "number") && (placedPrice > 0 || (placedPrice === 0 && !!placedOrderMealsList && placedOrderMealsList.some(pm => (pm.val().key != menu1Key) || !!pm.val().orderItemRemark || orderRemarks?.length > 0)));

    const recipientsWithPlacedContent = useMemo(() => {
        const keys = new Set<string>();
        placedOrderMealsList?.forEach(pm => {
            const recipientKey = pm.child('recipient').val();
            if (recipientKey) keys.add(recipientKey);
        });
        Object.values(placedOrder?.val()?.snacks ?? {}).forEach((snack: SnackLine) => {
            if (snack.recipient) keys.add(snack.recipient);
        });
        return keys;
    }, [placedOrderMealsList, placedOrder]);

    const showClosureAlert = useMemo(() => hasClosureAffectingPlacedOrder(
        day,
        schoolClosedRangeRecords,
        classroomClosedRangeRecords,
        hasPlacedOrder,
        recipientsWithPlacedContent,
        (recipientKey) => {
            const person = orderPeopleList?.find(p => p.key === recipientKey)?.val() as Student | undefined;
            return person?.student ? person.classroom : undefined;
        },
    ), [day, schoolClosedRangeRecords, classroomClosedRangeRecords, hasPlacedOrder, recipientsWithPlacedContent, orderPeopleList]);

    const userOrderMeals = useDatabase(day && userOrdersMealsPath(uid, day) || null);
    const userOrderMealsList: DataSnapshot<MealLine>[] = useList(userOrderMeals);
    const userOrderSnacks = useDatabase(day && userOrdersSnacksPath(uid, day) || null);
    const userOrderSnacksList: DataSnapshot<SnackLine>[] = useList(userOrderSnacks);

    const numPackages2 = userOrderMealsList?.filter(order => !!order.child('isPackage').val())?.length;
    const orderOtherThanPackage = hasOrderRemark || userOrderSnacksList?.length > 0 || userOrderMealsList?.filter(order => !order.child('isPackage').val() || order.val().key != menu1Key || !!order.val().orderItemRemark)?.length > 0;
    const isEditingOrder = !hasPlacedOrder && orderOtherThanPackage;

    const changingOrderHasUnsubmittedChanges = useMemo(
        () => hasUnsubmittedOrderChanges(
            isChangingOrder,
            userOrder?.val() as Order | undefined,
            placedOrder?.val() as Order | undefined,
        ),
        [isChangingOrder, userOrder, placedOrder],
    );
    const changeOrderIsEmptyDraft = useMemo(
        () => isChangeOrderEmptyDraft(
            isChangingOrder,
            userOrder?.val() as Order | undefined,
            placedOrder?.val() as Order | undefined,
        ),
        [isChangingOrder, userOrder, placedOrder],
    );

    // Determine order status for icon display
    const getOrderStatusIcon = () => {
        if (showClosureAlert) {
            return (
                <span className="mobile-closure-icon" data-status="closed" aria-label={orderStrings.dayClosureAlertAria}>
                    <ClosureAttentionIcon iconOnly />
                </span>
            );
        }
        if (hasPlacedOrder) {
            return <CheckIcon style={{ fontSize: '16px' }} data-status="completed" />;
        } else if (isChangingOrder) {
            return (
                <EditIcon
                    style={{ fontSize: '16px' }}
                    data-status={changeOrderIsEmptyDraft || !changingOrderHasUnsubmittedChanges ? 'editing-no-changes' : 'editing-staged'}
                />
            );
        } else if (isEditingOrder) {
            return <EditIcon style={{ fontSize: '16px' }} data-status="editing" />;
        } else if (cannotOrder && isEditingOrder) {
            return <CloseIcon style={{ fontSize: '16px' }} data-status="incomplete" />;
        }
        return null;
    };

    const dayAriaLabel = orderStrings.daySelectorDayAria(dayName, dayOfMonth, monthName, selected);

    const statusIcon = getOrderStatusIcon();

    return <>
    <button
        type="button"
        ref={mergeDayButtonRef}
        className={`day-selector-day${selected ? " selected" : ""}${cannotOrder ? " past" : ""}`}
        onClick={() => { if (!navigationDisabled && onClick) onClick(day); }}
        disabled={navigationDisabled}
        aria-label={dayAriaLabel}
        aria-current={selected ? "date" : undefined}
    >
        <div className="package-rect" style={{ visibility: numPackages2 > 0 ? "visible" : "hidden" }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="10" height="10" style={{ marginTop: '2px' }}><path fill="none" d="M0 0h24v24H0z" /><path d="M3 10H2V4.003C2 3.449 2.455 3 2.992 3h18.016A.99.99 0 0 1 22 4.003V10h-1v10.001a.996.996 0 0 1-.993.999H3.993A.996.996 0 0 1 3 20.001V10zm16 0H5v9h14v-9zM4 5v3h16V5H4zm5 7h6v2H9v-2z" style={{ fill: "white" }} /></svg>
            <span className="text">x {numPackages2}</span>
        </div>

        {/* Desktop version - show full date */}
        <div className="desktop-date">{dayName} {dayOfMonth} {monthName}</div>

        {/* Mobile version - show short date */}
        <div className="mobile-date">
            <div className="day-name">{dayName}</div>
            <div className="day-number">{dayOfMonth}</div>
        </div>

        {/* Desktop status tags */}
        <div className="desktop-tags">
            {showClosureAlert && <div className="tag" style={{ visibility: 'visible' }} aria-label={orderStrings.dayClosureAlertAria}>
                <span className="desktop-closure-icon">
                    <ClosureAttentionIcon iconOnly />
                </span>
                <span className='closed'>{orderStrings.statusClosed}</span>
            </div>
            }
            {<div className="tag" style={{ visibility: !showClosureAlert && (hasPlacedOrder || isEditingOrder || isChangingOrder) ? 'visible' : 'hidden' }}>
                {!showClosureAlert && hasPlacedOrder &&
                    <>
                        <img src="/img-assets/check.svg" width={14} height={14} alt="" aria-hidden="true" className="day-selector-tag-status-icon" />
                        <span className='completed'>{orderStrings.statusCompleted}</span>
                    </>
                    || cannotOrder &&
                    <div style={{ opacity: '0.4' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14"><path fill="none" d="M0 0h24v24H0z" /><path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-11.414L9.172 7.757 7.757 9.172 10.586 12l-2.829 2.828 1.415 1.415L12 13.414l2.828 2.829 1.415-1.415L13.414 12l2.829-2.828-1.415-1.415L12 10.586z" style={{ fill: "var(--black_txt)" }} /></svg>
                        <span className='not-completed'>{orderStrings.statusIncomplete}</span>
                    </div>
                    || isChangingOrder &&
                    <>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14"><path fill="none" d="M0 0h24v24H0z" /><path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-1-7v2h2v-2h-2zm0-8v6h2V7h-2z" style={{ fill: changeOrderIsEmptyDraft || !changingOrderHasUnsubmittedChanges ? 'var(--lb_orange)' : 'var(--red_error)' }} /></svg>
                        <span className={changingOrderHasUnsubmittedChanges && !changeOrderIsEmptyDraft ? 'editing' : 'editing editing--no-changes-yet'}>
                            {changeOrderIsEmptyDraft && orderStrings.statusChangingOrderEmptyInvalid}
                            {!changeOrderIsEmptyDraft && changingOrderHasUnsubmittedChanges && orderStrings.statusChangingOrderInBasket}
                            {!changeOrderIsEmptyDraft && !changingOrderHasUnsubmittedChanges && orderStrings.statusChangingOrderNoEdits}
                        </span>
                    </>
                    || <>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14"><path fill="none" d="M0 0h24v24H0z" /><path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-1-7v2h2v-2h-2zm0-8v6h2V7h-2z" style={{ fill: "var(--red_error)" }} /></svg>
                        <span className='editing'>{orderStrings.statusAwaitingSubmit}</span>
                    </>
                }
            </div>
            }
        </div>

    </button>
    <DayMobileStatusPortal anchorRef={dayButtonLocalRef} layoutKey={day}>
        {statusIcon}
    </DayMobileStatusPortal>
    </>;
}
